/**
 * NOS-214: Guided post-import review — the importer's deliberate deviations
 * rendered as decision cards: what the source had, what NOS did, why (benefit-
 * framed), and a choice. "Choose for me" accepts the recommended default per
 * decision, and "Choose for me on everything" accepts all in one click.
 *
 * Visual tone is deliberately distinct from the amber error/warning blocks —
 * these are features, not failures. Applying a choice writes through the
 * existing builder/customization paths (importDecisionApply); reverting later
 * is normal builder editing.
 */
import { useCallback, useMemo, useState } from "react"
import { Check, Sparkles } from "lucide-react"
import { useCustomizationStore } from "@/stores/customizationStore"
import type { ImportDecision } from "@/hooks/useImportJob"
import { applyImportDecisionChoice } from "@/lib/importDecisionApply"

const STORAGE_PREFIX = "nos-import-decisions"

export interface ImportDecisionsBlockProps {
  decisions: ImportDecision[]
  /** Job id — keys the persisted choice state so review can resume. */
  jobId: string | null
  orgId: string | null
  /** Targeting list for the posts decision (importedPostSlugsFromJob). */
  importedPostSlugs?: string[]
  /** Called after a choice writes changes (lets the builder refresh). */
  onApplied?: () => void
}

type CardStatus = "idle" | "applying" | "done" | "error"

interface CardState {
  optionId: string | null
  status: CardStatus
}

function storageKey(jobId: string | null): string {
  return `${STORAGE_PREFIX}-${jobId ?? "unknown"}`
}

/** Persisted {decisionKey → chosen optionId} — survives reloads (NOS-180 lesson). */
export function loadStoredChoices(jobId: string | null): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey(jobId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string") out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function storeChoice(jobId: string | null, key: string, optionId: string): void {
  try {
    const all = loadStoredChoices(jobId)
    all[key] = optionId
    localStorage.setItem(storageKey(jobId), JSON.stringify(all))
  } catch {
    /* ignore — choice state is a convenience, not a source of truth */
  }
}

export function ImportDecisionsBlock({
  decisions,
  jobId,
  orgId,
  importedPostSlugs = [],
  onApplied,
}: ImportDecisionsBlockProps) {
  const updateNavCtaText = useCustomizationStore((s) => s.updateNavCtaText)
  const currentNavCtaText = useCustomizationStore(
    (s) => s.customizations?.nav_cta_text ?? "Donate"
  )
  // NOS-239: announcement_bar decision applies through the store's own path
  const updateAnnouncement = useCustomizationStore((s) => s.updateAnnouncement)
  const currentAnnouncement = useCustomizationStore(
    (s) => (s.customizations?.announcement ?? null) as Record<string, unknown> | null
  )

  const stored = useMemo(() => loadStoredChoices(jobId), [jobId])
  const [cards, setCards] = useState<Record<string, CardState>>(() => {
    const initial: Record<string, CardState> = {}
    for (const d of decisions) {
      initial[d.key] = stored[d.key]
        ? { optionId: stored[d.key], status: "done" }
        : { optionId: null, status: "idle" }
    }
    return initial
  })
  const [acceptingAll, setAcceptingAll] = useState(false)

  const choose = useCallback(
    async (decision: ImportDecision, optionId: string): Promise<boolean> => {
      if (!orgId) return false
      setCards((prev) => ({ ...prev, [decision.key]: { optionId, status: "applying" } }))
      try {
        const result = await applyImportDecisionChoice(decision, optionId, {
          orgId,
          currentNavCtaText,
          updateNavCtaText,
          importedPostSlugs,
          currentAnnouncement,
          updateAnnouncement,
        })
        setCards((prev) => ({ ...prev, [decision.key]: { optionId, status: "done" } }))
        storeChoice(jobId, decision.key, optionId)
        if (result.changed > 0) onApplied?.()
        return true
      } catch (err) {
        console.error("[ImportDecisionsBlock] apply failed:", decision.key, err)
        setCards((prev) => ({ ...prev, [decision.key]: { optionId, status: "error" } }))
        return false
      }
    },
    [
      orgId,
      currentNavCtaText,
      updateNavCtaText,
      importedPostSlugs,
      currentAnnouncement,
      updateAnnouncement,
      jobId,
      onApplied,
    ]
  )

  const unresolved = decisions.filter((d) => cards[d.key]?.status !== "done")

  const acceptAll = useCallback(async () => {
    setAcceptingAll(true)
    try {
      for (const d of decisions) {
        if (cards[d.key]?.status === "done") continue
        await choose(d, d.recommended)
      }
    } finally {
      setAcceptingAll(false)
    }
  }, [decisions, cards, choose])

  if (decisions.length === 0) return null

  return (
    <div style={BLOCK_STYLE} data-testid="import-decisions">
      <div style={BLOCK_TITLE_STYLE}>
        <Sparkles size={12} style={{ flexShrink: 0 }} />
        Choices we made for you
      </div>
      <div style={{ opacity: 0.75, marginBottom: 8 }}>
        We applied a few nonprofit best practices during import. Keep them or switch back —
        your call.
      </div>
      {unresolved.length > 0 && (
        <button
          type="button"
          onClick={() => void acceptAll()}
          disabled={acceptingAll}
          style={{ ...ACCEPT_ALL_BTN_STYLE, opacity: acceptingAll ? 0.6 : 1 }}
        >
          <Check size={12} />
          {acceptingAll
            ? "Applying…"
            : `Choose for me${unresolved.length > 1 ? ` (accept all ${unresolved.length})` : ""}`}
        </button>
      )}
      {decisions.map((d) => (
        <DecisionCard
          key={d.key}
          decision={d}
          state={cards[d.key] ?? { optionId: null, status: "idle" }}
          onChoose={(optionId) => void choose(d, optionId)}
        />
      ))}
    </div>
  )
}

function DecisionCard({
  decision,
  state,
  onChoose,
}: {
  decision: ImportDecision
  state: CardState
  onChoose: (optionId: string) => void
}) {
  const chosen =
    state.status === "done" && state.optionId
      ? decision.options.find((o) => o.id === state.optionId) ?? null
      : null

  return (
    <div style={CARD_STYLE}>
      <div style={CARD_TITLE_STYLE}>{decision.title}</div>
      <div style={{ color: "rgba(255,255,255,0.75)", marginBottom: 3 }}>
        {decision.what_changed}
      </div>
      <div style={WHY_STYLE}>{decision.reasoning}</div>
      <div style={STATE_ROW_STYLE}>
        <span style={STATE_LABEL_STYLE}>Old site</span> {decision.source_state}
      </div>
      <div style={{ ...STATE_ROW_STYLE, marginBottom: 7 }}>
        <span style={STATE_LABEL_STYLE}>New site</span> {decision.nos_state}
      </div>

      {chosen ? (
        <div>
          <div style={CHOSEN_STYLE}>
            <Check size={11} style={{ flexShrink: 0 }} />
            {chosen.label}
          </div>
          {chosen.note && (
            <div style={chosen.id === decision.recommended ? NOTE_STYLE : CAUTION_STYLE}>
              {chosen.note}
            </div>
          )}
          <div style={{ opacity: 0.5, marginTop: 3 }}>
            You can change this anytime in the builder.
          </div>
        </div>
      ) : (
        <div>
          {state.status === "error" && (
            <div style={{ ...CAUTION_STYLE, marginBottom: 5 }}>
              Couldn&apos;t apply that choice — try again.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {decision.options.map((o) => {
              const isRecommended = o.id === decision.recommended
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onChoose(o.id)}
                  disabled={state.status === "applying"}
                  style={{
                    ...(isRecommended ? OPTION_PRIMARY_STYLE : OPTION_SECONDARY_STYLE),
                    opacity: state.status === "applying" ? 0.6 : 1,
                  }}
                >
                  <span style={{ flex: 1, textAlign: "left" }}>{o.label}</span>
                  {isRecommended && <span style={RECOMMENDED_PILL_STYLE}>Recommended</span>}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => onChoose(decision.recommended)}
            disabled={state.status === "applying"}
            style={CHOOSE_FOR_ME_STYLE}
          >
            {state.status === "applying" ? "Applying…" : "Choose for me"}
          </button>
        </div>
      )}
    </div>
  )
}

// === styles — teal/emerald "feature" tone, distinct from amber warnings ===

const BLOCK_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "10px 12px",
  background: "rgba(16,185,129,0.07)",
  border: "1px solid rgba(16,185,129,0.28)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const BLOCK_TITLE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 11,
  color: "#34d399",
  marginBottom: 4,
}

const ACCEPT_ALL_BTN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  width: "100%",
  marginBottom: 8,
  padding: "7px 10px",
  background: "rgba(16,185,129,0.35)",
  border: "1px solid rgba(16,185,129,0.55)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
}

const CARD_STYLE: React.CSSProperties = {
  padding: "8px 0 10px",
  borderTop: "1px solid rgba(16,185,129,0.18)",
}

const CARD_TITLE_STYLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 11,
  color: "rgba(255,255,255,0.9)",
  marginBottom: 3,
}

const WHY_STYLE: React.CSSProperties = {
  color: "rgba(255,255,255,0.55)",
  fontStyle: "italic",
  marginBottom: 6,
}

const STATE_ROW_STYLE: React.CSSProperties = {
  color: "rgba(255,255,255,0.6)",
  padding: "1px 0",
}

const STATE_LABEL_STYLE: React.CSSProperties = {
  display: "inline-block",
  minWidth: 46,
  fontWeight: 600,
  color: "rgba(255,255,255,0.4)",
  textTransform: "uppercase" as const,
  fontSize: 9,
}

const OPTION_BASE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  padding: "6px 8px",
  borderRadius: 6,
  fontSize: 10.5,
  fontWeight: 500,
  cursor: "pointer",
}

const OPTION_PRIMARY_STYLE: React.CSSProperties = {
  ...OPTION_BASE,
  background: "rgba(16,185,129,0.22)",
  border: "1px solid rgba(16,185,129,0.5)",
  color: "#fff",
}

const OPTION_SECONDARY_STYLE: React.CSSProperties = {
  ...OPTION_BASE,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "rgba(255,255,255,0.8)",
}

const RECOMMENDED_PILL_STYLE: React.CSSProperties = {
  flexShrink: 0,
  padding: "1px 6px",
  borderRadius: 999,
  background: "rgba(16,185,129,0.3)",
  color: "#6ee7b7",
  fontSize: 8.5,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 0.4,
}

const CHOOSE_FOR_ME_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#6ee7b7",
  cursor: "pointer",
  fontSize: 10,
  textDecoration: "underline",
  padding: "4px 0 0",
}

const CHOSEN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  color: "#4ade80",
  fontWeight: 600,
  fontSize: 11,
}

const NOTE_STYLE: React.CSSProperties = {
  marginTop: 3,
  color: "rgba(255,255,255,0.55)",
}

/** Cautionary note (e.g. Donate removed from the menu) — amber, can't be missed. */
const CAUTION_STYLE: React.CSSProperties = {
  marginTop: 3,
  padding: "5px 8px",
  background: "rgba(245,158,11,0.1)",
  border: "1px solid rgba(245,158,11,0.3)",
  borderRadius: 6,
  color: "#fbbf24",
}
